
import { useState, useEffect } from "react";
import { ModuleList } from "@/components/course/ModuleList";
import { LessonList } from "@/components/course/LessonList";
import { LessonEditor } from "@/components/course/LessonEditor";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Module, Lesson } from "@/types/course";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface CourseContentTabProps {
  courseId: string;
  modules: Module[];
  isLoadingModules: boolean;
  onModulesChange: (modules: Module[]) => void;
}

export const CourseContentTab = ({ 
  courseId, 
  modules, 
  isLoadingModules,
  onModulesChange 
}: CourseContentTabProps) => {
  const queryClient = useQueryClient();
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);

  useEffect(() => {
    if (modules.length > 0 && !selectedModuleId) {
      setSelectedModuleId(modules[0].id);
    }
  }, [modules, selectedModuleId]);

  const handleSelectModule = (moduleId: string) => {
    setSelectedModuleId(moduleId);
    setSelectedLessonId(null);
    setIsCreatingLesson(false);
  };

  const handleSelectLesson = (lessonId: string | null) => {
    setSelectedLessonId(lessonId);
    setIsCreatingLesson(false);
  };

  const handleAddLesson = () => {
    setSelectedLessonId(null);
    setIsCreatingLesson(true);
  };

  const handleLessonChange = (updatedLesson: Lesson) => {
    const updatedModules = modules.map(module => {
      if (module.id === selectedModuleId) {
        const existingLessonIndex = module.lessons?.findIndex(l => l.id === updatedLesson.id);
        
        if (existingLessonIndex !== undefined && existingLessonIndex >= 0) {
          const updatedLessons = [...(module.lessons || [])];
          updatedLessons[existingLessonIndex] = updatedLesson;
          return { ...module, lessons: updatedLessons };
        } else {
          return {
            ...module,
            lessons: [...(module.lessons || []), updatedLesson]
          };
        }
      }
      return module;
    });
    
    onModulesChange(updatedModules);
    
    // Automatically close the lesson editor after saving
    setSelectedLessonId(null);
    setIsCreatingLesson(false);
    
    queryClient.invalidateQueries({
      queryKey: ['modules', courseId],
    });
    
    toast.success("Lesson saved successfully");
  };

  const handleLessonDelete = () => {
    setSelectedLessonId(null);
    
    queryClient.invalidateQueries({
      queryKey: ['modules', courseId],
    });
  };

  const handleCreateFirstModule = () => {
    // Get the actual button in the ModuleList component
    const moduleAddButton = document.querySelector('button[aria-label="Add module"]');
    if (moduleAddButton && 'click' in moduleAddButton) {
      (moduleAddButton as HTMLElement).click();
    } else {
      // Fallback if button not found with aria-label
      const addButtons = document.querySelectorAll('button');
      for (const button of addButtons) {
        if (button.textContent?.includes('Add Module')) {
          button.click();
          break;
        }
      }
    }
  };

  const currentModule = modules.find(m => m.id === selectedModuleId);
  const currentLesson = currentModule?.lessons?.find(l => l.id === selectedLessonId);

  return (
    <div className="flex border rounded-lg overflow-hidden">
      <div className="w-[300px] border-r bg-muted/20">
        <div className="p-4">
          <ModuleList 
            courseId={courseId}
            modules={modules}
            onModulesChange={onModulesChange}
            selectedModuleId={selectedModuleId}
            onSelectModule={handleSelectModule}
          />
        </div>
      </div>
      
      <div className="flex-1 min-h-[600px]">
        {!selectedModuleId ? (
          <div className="h-full flex items-center justify-center flex-col p-8">
            <h3 className="text-lg font-medium mb-2">No Module Selected</h3>
            <p className="text-muted-foreground text-center mb-4">
              Select a module from the sidebar or create a new one to start adding lessons.
            </p>
            {modules.length === 0 && (
              <Button onClick={handleCreateFirstModule}>
                Create Your First Module
              </Button>
            )}
          </div>
        ) : isLoadingModules ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="p-6">
            {isCreatingLesson ? (
              <LessonEditor
                moduleId={selectedModuleId}
                lesson={null}
                onLessonChange={handleLessonChange}
                onLessonDelete={handleLessonDelete}
                isNew={true}
              />
            ) : selectedLessonId && currentLesson ? (
              <LessonEditor
                moduleId={selectedModuleId}
                lesson={currentLesson}
                onLessonChange={handleLessonChange}
                onLessonDelete={handleLessonDelete}
              />
            ) : (
              <LessonList
                module={currentModule || null}
                selectedLessonId={selectedLessonId}
                onSelectLesson={handleSelectLesson}
                onAddLesson={handleAddLesson}
                onUpdateLesson={handleLessonChange}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
